import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';

/**
 * For owner routes (except onboarding): if the owner hasn't completed Stripe Connect,
 * show a friendly modal prompting them to finish setup before they can accept payments.
 * Does not block navigation; owners can dismiss and continue (e.g. to complete onboarding).
 */
export default function OwnerStripeGate({ children }) {
  const location = useLocation();
  const { getToken } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const isOnboarding = location.pathname.includes('/onboarding');

  const checkStripe = useCallback(async () => {
    if (isOnboarding) {
      setLoading(false);
      return;
    }
    try {
      const res = await api('/api/owners/me', { getToken });
      if (!res.ok) {
        setLoading(false);
        return;
      }
      const data = await res.json();
      const business = data?.business;
      // Show modal if owner has business but Stripe Connect is incomplete:
      // - No stripeAccountId, OR
      // - stripeAccountId exists but stripeAccountComplete is false (restricted/incomplete account)
      const hasStripe = !!(business?.stripeAccountId?.trim());
      const stripeComplete = business?.stripeAccountComplete === true;
      if (business && (!hasStripe || !stripeComplete) && !dismissed) {
        setShowModal(true);
      }
    } catch {
      // Ignore; don't block owner experience (e.g. 403 if no business yet)
    } finally {
      setLoading(false);
    }
  }, [getToken, isOnboarding, dismissed]);

  useEffect(() => {
    checkStripe();
  }, [checkStripe]);

  const handleCompleteSetup = async () => {
    setLinking(true);
    try {
      const res = await api('/api/owners/me/connect-account-link', {
        getToken,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      const d = await res.json();
      if (res.ok && d?.url) {
        window.location.href = d.url;
        return;
      }
      throw new Error(d?.error || 'Could not start setup');
    } catch (err) {
      setLinking(false);
      alert(err.message || 'Something went wrong. Please try again.');
    }
  };

  const handleDismiss = () => {
    setShowModal(false);
    setDismissed(true);
  };

  if (isOnboarding || loading) {
    return children;
  }

  return (
    <>
      {children}
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={handleDismiss} aria-hidden />
          <div
            className="relative w-full max-w-md rounded-2xl bg-white shadow-xl p-6 space-y-4"
            role="dialog"
            aria-labelledby="stripe-modal-title"
            aria-modal="true"
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-2xl">
                💳
              </div>
              <div>
                <h2 id="stripe-modal-title" className="text-lg font-semibold text-slate-900">
                  Payment setup required
                </h2>
                <p className="mt-1 text-slate-600 text-sm">
                  Complete Stripe Connect before you can accept payments from customers. It only takes a few minutes.
                </p>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleCompleteSetup}
                disabled={linking}
                className="flex-1 py-3 rounded-xl bg-orange-600 hover:bg-orange-500 disabled:opacity-70 text-white font-semibold transition-colors"
              >
                {linking ? 'Opening…' : 'Finish Stripe setup'}
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="px-4 py-3 rounded-xl border border-slate-300 text-slate-600 hover:bg-slate-50 font-medium transition-colors"
              >
                Later
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
